import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import ImageResize from "tiptap-extension-resize-image";
import Link from "@tiptap/extension-link";
import Youtube from "@tiptap/extension-youtube";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Bold, Italic, List, ListOrdered, Quote, Heading1, Heading2,
  Link as LinkIcon, Image as ImageIcon, Youtube as YtIcon, Undo2, Redo2, Pilcrow,
} from "lucide-react";

const Btn = ({ onClick, active, title, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    data-testid={testid}
    className={active ? "is-active" : ""}
  >
    {children}
  </button>
);

const Editor = ({ value, onChange }) => {
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);

  const uploadAndInsert = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const tid = toast.loading("caricamento immagine…");
    try {
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      editorRef.current?.chain().focus().setImage({ src: fullUrl, alt: file.name || "immagine" }).run();
      toast.success("Immagine inserita", { id: tid });
    } catch (err) {
      toast.error("Errore upload: " + (err?.response?.data?.detail || err.message), { id: tid });
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      ImageResize,
      Link.configure({ openOnClick: false, autolink: true }),
      Youtube.configure({ controls: true, nocookie: true, width: 720, height: 405 }),
      Placeholder.configure({ placeholder: "Inizia a scrivere…" }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      // Image paste: from clipboard (right-click → Copy Image on a webpage,
      // screenshots, drag-from-Finder etc.) → upload to our storage and insert.
      handlePaste(view, event) {
        const items = event.clipboardData?.items || [];
        const imgItems = Array.from(items).filter(
          (it) => it.kind === "file" && it.type && it.type.startsWith("image/")
        );
        if (imgItems.length > 0) {
          event.preventDefault();
          imgItems.forEach((it) => {
            const f = it.getAsFile();
            if (f) uploadAndInsert(f);
          });
          return true;
        }
        // Also handle event.clipboardData.files (some browsers)
        const files = event.clipboardData?.files;
        if (files && files.length > 0) {
          const imgs = Array.from(files).filter((f) => f.type && f.type.startsWith("image/"));
          if (imgs.length > 0) {
            event.preventDefault();
            imgs.forEach(uploadAndInsert);
            return true;
          }
        }
        return false;
      },
      // Drag&drop image files into the editor → upload + insert
      handleDrop(view, event) {
        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
          const imgs = Array.from(files).filter((f) => f.type && f.type.startsWith("image/"));
          if (imgs.length > 0) {
            event.preventDefault();
            imgs.forEach(uploadAndInsert);
            return true;
          }
        }
        return false;
      },
      // Pasting plain text: every line becomes its own paragraph
      transformPastedText(text) {
        if (!text) return text;
        const normalized = text.replace(/\r\n?/g, "\n");
        const lines = normalized.split("\n").map((l) => l.trim());
        // collapse runs of empty lines into a single blank so ProseMirror
        // gets paragraph separators between chunks
        const out = [];
        let prevEmpty = false;
        for (const ln of lines) {
          if (!ln) {
            if (!prevEmpty && out.length) out.push("");
            prevEmpty = true;
          } else {
            out.push(ln);
            prevEmpty = false;
          }
        }
        // separate every chunk with a blank line so PM makes paragraphs
        return out.join("\n\n");
      },
      // Pasting HTML (e.g. from Word): convert <br> into paragraph breaks
      transformPastedHTML(html) {
        if (!html) return html;
        return html
          .replace(/<br\s*\/?>/gi, "</p><p>")
          .replace(/<p>\s*<\/p>/gi, "");
      },
    },
  });

  useEffect(() => {
    editorRef.current = editor;
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return <div className="tiptap-shell" style={{ minHeight: 400 }} />;

  const addLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL del link", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const addYoutube = () => {
    const url = window.prompt("URL YouTube da incorporare", "https://www.youtube.com/watch?v=");
    if (!url) return;
    editor.chain().focus().setYoutubeVideo({ src: url }).run();
  };

  const onUploadImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      editor.chain().focus().setImage({ src: fullUrl, alt: f.name }).run();
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error("Errore upload: " + (err?.response?.data?.detail || err.message));
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div className="tiptap-shell" data-testid="editor-wrapper">
      <div className="tiptap-toolbar">
        <Btn testid="ed-bold" title="Grassetto" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold size={14} />
        </Btn>
        <Btn testid="ed-italic" title="Corsivo" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic size={14} />
        </Btn>
        <span className="sep" />
        <Btn testid="ed-h1" title="Titolo 1" active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
          <Heading1 size={14} />
        </Btn>
        <Btn testid="ed-h2" title="Titolo 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Heading2 size={14} />
        </Btn>
        <Btn testid="ed-p" title="Paragrafo" active={editor.isActive("paragraph")} onClick={() => editor.chain().focus().setParagraph().run()}>
          <Pilcrow size={14} />
        </Btn>
        <span className="sep" />
        <Btn testid="ed-ul" title="Elenco" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List size={14} />
        </Btn>
        <Btn testid="ed-ol" title="Elenco numerato" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered size={14} />
        </Btn>
        <Btn testid="ed-quote" title="Citazione" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <Quote size={14} />
        </Btn>
        <span className="sep" />
        <Btn testid="ed-link" title="Link" active={editor.isActive("link")} onClick={addLink}>
          <LinkIcon size={14} />
        </Btn>
        <Btn testid="ed-image" title="Immagine" onClick={() => fileInputRef.current?.click()}>
          <ImageIcon size={14} />
        </Btn>
        <Btn testid="ed-youtube" title="YouTube" onClick={addYoutube}>
          <YtIcon size={14} />
        </Btn>
        <span className="sep" />
        <Btn testid="ed-undo" title="Annulla" onClick={() => editor.chain().focus().undo().run()}>
          <Undo2 size={14} />
        </Btn>
        <Btn testid="ed-redo" title="Ripeti" onClick={() => editor.chain().focus().redo().run()}>
          <Redo2 size={14} />
        </Btn>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onUploadImage}
          data-testid="ed-image-input"
        />
      </div>
      <EditorContent editor={editor} data-testid="editor-content" />
    </div>
  );
};

export default Editor;
